import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  HolderOutlined,
  LockOutlined,
  MailOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, Empty, Spin, Tag } from 'antd';
import { useProfile } from '../../profile/components/ProfileContext';
import { UtilService } from '../../../../../core/services/util.service';
import { StagesService } from '../services/stages.service';
import { StageId } from '../models/stages.model';
import { route } from '../../../../../constants/routes';
import './HiringStages.css';

interface StagesListProps {
  clientName: string;
}

interface SortableStageProps {
  stage: StageId;
  position: number;
  canEdit: boolean;
  onOpen: (stage: StageId) => void;
}

const stageIsDefault = (stage: StageId & { default?: boolean }) =>
  Boolean(stage.default || stage.isDefault);

const SortableStage: React.FC<SortableStageProps> = ({
  stage,
  position,
  canEdit,
  onOpen,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id, disabled: !canEdit });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`hiring-stage-row ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        className="hiring-stage-row__handle"
        aria-label={`Reorder ${stage.name}`}
        disabled={!canEdit}
        {...attributes}
        {...listeners}
      >
        <HolderOutlined aria-hidden />
      </button>
      <span className="hiring-stage-row__order" aria-hidden>
        {String(position).padStart(2, '0')}
      </span>
      <div className="hiring-stage-row__content">
        <strong>{stage.name}</strong>
        <p>
          {stage.description ||
            'Add guidance so recruiters use this stage consistently.'}
        </p>
      </div>
      <div className="hiring-stage-row__templates">
        <MailOutlined aria-hidden />
        <span>
          <strong>{stage.templateCount || 0}</strong>{' '}
          {(stage.templateCount || 0) === 1 ? 'template' : 'templates'}
        </span>
      </div>
      <Tag icon={<LockOutlined aria-hidden />} color="blue">
        {stageIsDefault(stage as StageId & { default?: boolean })
          ? 'Default'
          : 'Protected'}
      </Tag>
      <Button
        icon={<MailOutlined />}
        onClick={() => onOpen(stage)}
        aria-label={`${canEdit ? 'Manage templates for' : 'View'} ${stage.name}`}
      >
        {canEdit ? 'Manage' : 'View'}
      </Button>
    </li>
  );
};

const StagesList: React.FC<StagesListProps> = ({ clientName }) => {
  const navigate = useNavigate();
  const { companyId, isAdmin } = useProfile();
  const { id: clientId } = useParams();
  const [stages, setStages] = useState<StageId[]>([]);
  const [savedOrder, setSavedOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getStages = useCallback(async () => {
    if (!companyId || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await StagesService.GetStages(companyId, clientId, {
        size: 100,
        sort: 'statusOrder',
        filter: 'deleted:eq:false',
      });
      const mappedStages = (result?.content ?? [])
        .filter((item: any) => item.name?.trim().toLowerCase() !== 'custom')
        .map((item: any) => ({
          ...item,
          name: item.name || '',
          description: item.description || '',
          isDefault: Boolean(item.default || item.isDefault),
          templateCount: Number(item.templateCount || 0),
          statusOrder: Number(item.statusOrder || 0),
        }));
      setStages(mappedStages);
      setSavedOrder(mappedStages.map((stage: StageId) => stage.id));
      setStatusMessage('');
    } catch (error) {
      console.error('Failed to fetch hiring stages:', error);
      setStages([]);
      UtilService.Alert(
        'Hiring stages unavailable',
        'error',
        'The hiring workflow could not be loaded. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, companyId]);

  useEffect(() => {
    void getStages();
  }, [getStages]);

  const orderChanged = useMemo(
    () =>
      stages.length === savedOrder.length &&
      stages.some((stage, index) => stage.id !== savedOrder[index]),
    [savedOrder, stages]
  );

  const openStage = (stage: StageId) => {
    if (!clientId) return;
    navigate(route(`/stage-screen/${clientId}/${stage.id}`), {
      state: { clientName, stageName: stage.name },
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setStages((current) => {
      const oldIndex = current.findIndex((stage) => stage.id === active.id);
      const newIndex = current.findIndex((stage) => stage.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return current;
      return arrayMove(current, oldIndex, newIndex);
    });
    setStatusMessage('Order changed. Save to publish the workflow.');
  };

  const saveOrder = async () => {
    if (!companyId || !clientId || !orderChanged || savingOrder) return;
    setSavingOrder(true);
    try {
      await StagesService.PatchOrder(
        companyId,
        clientId,
        stages.map((stage, index) => ({
          statusId: stage.id,
          order: index,
        }))
      );
      setSavedOrder(stages.map((stage) => stage.id));
      setStatusMessage('Workflow order saved.');
      UtilService.Alert(
        'Workflow updated',
        'success',
        'Recruiters will now see the new stage order.'
      );
    } catch (error) {
      console.error('Failed to reorder hiring stages:', error);
      UtilService.Alert(
        'Order not saved',
        'error',
        'The stage order could not be updated.'
      );
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) {
    return (
      <div
        className="hiring-stages__loading"
        role="status"
        aria-label="Loading hiring stages"
      >
        <Spin size="large" />
        <span>Loading hiring workflow…</span>
      </div>
    );
  }

  return (
    <section className="hiring-stages" aria-labelledby="hiring-stages-title">
      <header className="hiring-stages__header">
        <div>
          <span>Workflow configuration</span>
          <h2 id="hiring-stages-title">Hiring stages</h2>
          <p>Reorder the candidate journey and manage stage communication.</p>
          {isAdmin ? (
            <small>Drag stages to set the recruiter workflow.</small>
          ) : null}
        </div>
        {isAdmin ? (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={savingOrder}
            disabled={!orderChanged}
            onClick={() => void saveOrder()}
          >
            Save order
          </Button>
        ) : null}
      </header>

      <div className="hiring-stages__status" role="status" aria-live="polite">
        {statusMessage ||
          `${stages.length} protected stages · ${stages.reduce(
            (total, stage) => total + Number(stage.templateCount || 0),
            0
          )} message templates`}
      </div>

      {stages.length ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={stages.map((stage) => stage.id)}
            strategy={verticalListSortingStrategy}
          >
            <ol className="hiring-stages__list">
              {stages.map((stage, index) => (
                <SortableStage
                  key={stage.id}
                  stage={stage}
                  position={index + 1}
                  canEdit={isAdmin}
                  onOpen={openStage}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No active hiring stages are available."
        />
      )}
    </section>
  );
};

export default StagesList;
